import DayViewMapLegend from "./DayViewMapLegend"
import { useRef, useState, useEffect, useMemo } from "react"
import Map, { NavigationControl, MapRef, FullscreenControl, Source, Layer } from 'react-map-gl/mapbox'
import MapStyleDropdown from "./MapStyleDropdown"
import { CalendarEventVisit, DayViewEvent, Timesheet, Visit, VisitWorkareaStatusRecord } from "../scheduleApi"
import ScheduleMapMarker from "../Map/ScheduleMapMarker"
import ScheduleMapClusterMarker from "../Map/ScheduleMapClusterMarker"
import { getMarkerStatus, groupEventsByCrewLead, clusterMarkersByPosition, EventMarker } from "../Map/scheduleMapModel"
import { useSchedule } from "../ScheduleContext"
import { Feature, LineString } from 'geojson'
import { getDrivingDirections, getGeocoding } from "../../RouteManagement/Mappers/mapbox"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { EventAddressModal } from "./EventAddressModal"
import { Button, Spin, Tooltip } from "antd"
import { useUpdateJobPropertyGeolocationMutation, useUpdateJobPropertyGeolocationMutationNoInvalidate } from "../../RouteManagement/routingQueries"
import { useQueryClient } from 'react-query'
import { useTenantLocationsDataQuery } from "../../../domain/tenantLocations/tenantLocationsQueries"
import { findCrewMemberByEmail } from "../crewModel"
import { TenantLocationType } from "../../../domain/tenantLocations/tenantLocation"
import MapShopAddress from "../../RouteManagement/RouteView/MapShopAddress"
import ManageShopAddressModal from "../../RouteManagement/RouteAdd/ManageShopAddressModal"
import ShopMarker from "../../RouteManagement/RouteMap/ShopMarker"
import { Dayjs } from "dayjs"
import DayViewMarkerPopup from "./DayViewMarkerPopup"

type Props = {
  events: DayViewEvent[]
  visits: Visit[]
  calendarEventVisits: CalendarEventVisit[]
  workareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  date: Dayjs
  hoveredEventId: number | null
  hoveredEventIds: number[]
}

const DayViewMap = ({ events, visits, calendarEventVisits, workareaStatuses, timesheets, date, hoveredEventId, hoveredEventIds }: Props) => {
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-v9")
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [isStyleChanging, setIsStyleChanging] = useState(false)
  const [selectedEventForAddress, setSelectedEventForAddress] = useState<DayViewEvent | null>(null)
  const [resolvingEventId, setResolvingEventId] = useState<number | null>(null)
  const [isManageShopAddressModalOpen, setIsManageShopAddressModalOpen] = useState(false)
  const [selectedMarker, setSelectedMarker] = useState<EventMarker | null>(null)
  const [isAutoResolving, setIsAutoResolving] = useState(false)
  const [mapHoveredEventId, setMapHoveredEventId] = useState<number | null>(null)
  const [mapHoveredEventIds, setMapHoveredEventIds] = useState<number[]>([])
  const autoResolveAttemptedRef = useRef<Set<number>>(new Set())
  const isAutoResolvingRef = useRef(false)
  const mapRef = useRef<MapRef>(null)
  const { crew, isCrewLoading } = useSchedule()
  const { mutate: updateJobPropertyGeolocation } = useUpdateJobPropertyGeolocationMutation()
  const { mutateAsync: updateJobPropertyGeolocationAsync } = useUpdateJobPropertyGeolocationMutationNoInvalidate()
  const queryClient = useQueryClient()
  const { data: tenantLocations, isLoading: isTenantLocationsLoading } = useTenantLocationsDataQuery()
  const shopLocation = tenantLocations?.find(tl => tl.type === TenantLocationType.Shop)

  const DEFAULT_MAP_ZOOM = 10
  const initialViewState = shopLocation ? {
    longitude: shopLocation.longitude,
    latitude: shopLocation.latitude,
    zoom: DEFAULT_MAP_ZOOM
  } : {
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: DEFAULT_MAP_ZOOM
  }

  const hasValidCoordinates = (event: DayViewEvent): boolean =>
    event.latitude != null &&
    event.longitude != null &&
    Number.isFinite(event.latitude) &&
    Number.isFinite(event.longitude)

  const eventsWithCoordinates = events.filter(hasValidCoordinates)

  const eventsWithoutCoordinates = events.filter(event => !hasValidCoordinates(event))

  const markers = useMemo(() => {
    return eventsWithCoordinates.map(event => {
      const status = getMarkerStatus(event, visits, calendarEventVisits, timesheets, crew ?? [], date)
      const crewLead = !isCrewLoading && event.crewForemanEmail
        ? findCrewMemberByEmail(crew, event.crewForemanEmail)
        : null

      return {
        event,
        latitude: event.latitude!,
        longitude: event.longitude!,
        status,
        crewLeadAvatar: crewLead?.hasStockAvatar ? undefined : crewLead?.avatar,
        crewLeadName: crewLead?.name,
        crewLeadColor: crewLead ? (crewLead.color || '#000000') : null,
        eventName: event.jobName
      } as EventMarker
    })
  }, [eventsWithCoordinates, visits, calendarEventVisits, timesheets, date, crew, isCrewLoading])

  const { clusters, singles } = useMemo(() => {
    return clusterMarkersByPosition({ markers, visits, calendarEventVisits, workareaStatuses, date })
  }, [markers, visits, calendarEventVisits, workareaStatuses, date])

  const hoveredMarker = useMemo(() => {
    if (!hoveredEventId) return null
    return singles.find(m => m.event.id === hoveredEventId) ?? null
  }, [hoveredEventId, singles])

  const markerToShow = selectedMarker ?? hoveredMarker

  const crewLeadGroups = groupEventsByCrewLead(events, crew ?? undefined)
  const [routeData, setRouteData] = useState<Record<string, any>>({})

  useEffect(() => {
    if (!isMapLoaded) return

    let cancelled = false

    const fetchDirections = async () => {
      const newRouteData: Record<string, any> = {}

      for (const group of crewLeadGroups) {
        if (cancelled) return

        const plannedEvents = group.events.filter(event => {
          const status = getMarkerStatus(event, visits, calendarEventVisits, timesheets, crew ?? [], date)
          return status === 'planned'
        })

        const coords = plannedEvents
          .filter(event => event.latitude !== null && event.longitude !== null)
          .map(event => [event.longitude!, event.latitude!])

        if (coords.length >= 2) {
          try {
            const MAX_WAYPOINTS = 25
            let allCoordinates: number[][] = []

            if (coords.length <= MAX_WAYPOINTS) {
              const directions = await getDrivingDirections(coords)
              if (cancelled) return
              if (directions?.routes?.[0]?.geometry?.coordinates) {
                allCoordinates = directions.routes[0].geometry.coordinates
              }
            } else {
              for (let i = 0; i < coords.length - 1; i += MAX_WAYPOINTS - 1) {
                if (cancelled) return
                const batchCoords = coords.slice(i, i + MAX_WAYPOINTS)
                if (batchCoords.length < 2) break

                const directions = await getDrivingDirections(batchCoords)
                if (directions?.routes?.[0]?.geometry?.coordinates) {
                  const batchRoute = directions.routes[0].geometry.coordinates
                  if (allCoordinates.length > 0) {
                    allCoordinates = allCoordinates.concat(batchRoute.slice(1))
                  } else {
                    allCoordinates = batchRoute
                  }
                }
              }
            }

            if (allCoordinates.length > 0) {
              newRouteData[group.crewLeadEmail] = allCoordinates
            }
          } catch (error) {
          }
        }
      }

      if (!cancelled) {
        setRouteData(newRouteData)
      }
    }

    fetchDirections()

    return () => {
      cancelled = true
    }
  }, [events, visits, calendarEventVisits, date, timesheets, crew, isMapLoaded])

  useEffect(() => {
    if (!mapRef.current || !isMapLoaded) return

    if (eventsWithCoordinates.length === 0) {
      if (shopLocation?.latitude && shopLocation?.longitude) {
        mapRef.current.flyTo({
          center: [shopLocation.longitude, shopLocation.latitude],
          zoom: DEFAULT_MAP_ZOOM,
          duration: 1000,
        })
      }
      return
    }

    const bounds = eventsWithCoordinates.reduce((acc, event) => {
      if (event.latitude === null || event.longitude === null) return acc

      return {
        minLng: Math.min(acc.minLng, event.longitude),
        maxLng: Math.max(acc.maxLng, event.longitude),
        minLat: Math.min(acc.minLat, event.latitude),
        maxLat: Math.max(acc.maxLat, event.latitude),
      }
    }, {
      minLng: eventsWithCoordinates[0].longitude!,
      maxLng: eventsWithCoordinates[0].longitude!,
      minLat: eventsWithCoordinates[0].latitude!,
      maxLat: eventsWithCoordinates[0].latitude!,
    })

    if (
      !Number.isFinite(bounds.minLng) ||
      !Number.isFinite(bounds.maxLng) ||
      !Number.isFinite(bounds.minLat) ||
      !Number.isFinite(bounds.maxLat)
    ) {
      return
    }

    const padding = 150
    const container = mapRef.current.getContainer()
    if (container.clientWidth < padding * 2 || container.clientHeight < padding * 2) {
      return
    }

    mapRef.current.fitBounds(
      [[bounds.minLng, bounds.minLat], [bounds.maxLng, bounds.maxLat]],
      {
        padding,
        duration: 1000,
        maxZoom: 11,
      }
    )
  }, [eventsWithCoordinates.length, isMapLoaded, shopLocation])

  useEffect(() => {
    if (isAutoResolvingRef.current) return

    const resolveAutomatically = async () => {
      const eventsToResolve = events.filter(event =>
        (event.latitude === null || event.longitude === null) &&
        !autoResolveAttemptedRef.current.has(event.id)
      )

      if (eventsToResolve.length === 0) return

      isAutoResolvingRef.current = true
      setIsAutoResolving(true)

      for (const event of eventsToResolve) {
        autoResolveAttemptedRef.current.add(event.id)

        const addressString = [
          event.customerAddress1,
          event.customerAddress2,
          event.customerCity,
          event.customerState,
          event.customerZip
        ].filter(Boolean).join(' ')

        if (!addressString.trim() || !event.propertyId) {
          continue
        }

        try {
          const result = await getGeocoding(addressString)
          if (result) {
            const context = result.properties.context || {}
            const address = context.address?.name || result.properties.address_line1 || ''
            const city = context.place?.name || result.properties.context_city || ''
            const state = context.region?.region_code || result.properties.context_region || ''
            const zip = context.postcode?.name || result.properties.context_postcode || ''
            const county = context.district?.name || ''
            const country = context.country?.country_code || 'US'

            const latitude = result.geometry.coordinates[1]
            const longitude = result.geometry.coordinates[0]

            await updateJobPropertyGeolocationAsync({
              nickname: '',
              propertyId: event.propertyId,
              address1: address,
              address2: null,
              city: city,
              state: state,
              zip: zip,
              county: county,
              countryCode: country,
              latitude: latitude,
              longitude: longitude
            })
          }
        } catch (error) {
        }
      }

      queryClient.invalidateQueries(['scheduleEventsByDate'])
      isAutoResolvingRef.current = false
      setIsAutoResolving(false)
    }

    resolveAutomatically()
  }, [events, updateJobPropertyGeolocationAsync, queryClient])

  const handleResolveLocation = async (event: DayViewEvent) => {
    if (resolvingEventId === event.id) return

    setResolvingEventId(event.id)

    const addressString = [
      event.customerAddress1,
      event.customerAddress2,
      event.customerCity,
      event.customerState,
      event.customerZip
    ].filter(Boolean).join(' ')

    if (addressString.trim()) {
      const result = await getGeocoding(addressString)
      if (result && event.propertyId) {
        const context = result.properties.context || {}
        const address = context.address?.name || result.properties.address_line1 || ''
        const city = context.place?.name || result.properties.context_city || ''
        const state = context.region?.region_code || result.properties.context_region || ''
        const zip = context.postcode?.name || result.properties.context_postcode || ''
        const county = context.district?.name || ''
        const country = context.country?.country_code || 'US'

        const latitude = result.geometry.coordinates[1]
        const longitude = result.geometry.coordinates[0]

        updateJobPropertyGeolocation({
          propertyId: event.propertyId,
          nickname: '',
          address1: address,
          address2: null,
          city: city,
          state: state,
          zip: zip,
          county: county,
          countryCode: country,
          latitude: latitude,
          longitude: longitude
        })
        setResolvingEventId(null)
      } else {
        setResolvingEventId(null)
        setSelectedEventForAddress(event)
      }
    } else {
      setResolvingEventId(null)
      setSelectedEventForAddress(event)
    }
  }

  if (isTenantLocationsLoading) {
    return (
      <div style={{ flex: 1, position: 'relative', background: '#eee', overflow: 'hidden', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  return <div style={{ flex: 1, position: 'relative', background: '#eee', overflow: 'hidden' }}>
    <Map
      ref={mapRef}
      mapboxAccessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
      onLoad={() => {
        setIsMapLoaded(true)
      }}
      onStyleData={() => {
        if (isStyleChanging) {
          setIsStyleChanging(false)
        }
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle={mapStyle}
      initialViewState={initialViewState}
    >
      <NavigationControl position="top-left" />
      <FullscreenControl position="bottom-left" />

      {/* Satellite View Toggle */}
      <MapStyleDropdown
        mapStyle={mapStyle}
        setMapStyle={setMapStyle}
        setIsStyleChanging={setIsStyleChanging}
      />

      <MapShopAddress
        tenantLocations={tenantLocations}
        onManageShopAddress={() => setIsManageShopAddressModalOpen(true)}
      />

      {isAutoResolving && eventsWithoutCoordinates.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          backgroundColor: '#e6f7ff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          padding: '12px 16px',
          border: '1px solid #91d5ff',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Spin size="small" />
          <div style={{ fontSize: '13px', color: '#0050b3' }}>
            Resolving {eventsWithoutCoordinates.length} {eventsWithoutCoordinates.length === 1 ? 'address' : 'addresses'}...
          </div>
        </div>
      )}

      {!isAutoResolving && eventsWithoutCoordinates.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50px',
          zIndex: 1,
          backgroundColor: '#fff2f0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          padding: '12px 16px',
          border: '1px solid #ff4d4f',
          maxWidth: '500px',
          maxHeight: '300px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              style={{ color: '#ff4d4f', fontSize: '16px', marginTop: '2px' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 'bold',
                fontSize: '13px',
                color: '#a8071a'
              }}>
                {eventsWithoutCoordinates.length} {eventsWithoutCoordinates.length === 1 ? 'Event' : 'Events'} Could Not Be Resolved
              </div>
              <div style={{
                fontSize: '13px',
                color: '#a8071a',
                marginTop: '2px'
              }}>
                {eventsWithoutCoordinates.length === 1 ? 'This event requires' : 'These events require'} manual address resolution
              </div>
            </div>
          </div>
          <div style={{
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {eventsWithoutCoordinates.map(event => (
              <div
                key={event.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #ffccc7'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#000',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {event.jobName}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.65)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {event.customerDisplayName}
                  </div>
                </div>
                <Tooltip title="The address could not be automatically resolved. Click to manually select the correct location.">
                  <Button
                    size="small"
                    type="primary"
                    loading={resolvingEventId === event.id}
                    onClick={() => handleResolveLocation(event)}
                  >
                    Resolve
                  </Button>
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.entries(routeData).map(([crewLeadEmail, directions], index) => {
        const group = crewLeadGroups.find(g => g.crewLeadEmail === crewLeadEmail)

        if (!group || !directions || directions.length < 2) return null

        const routeGeoJSON: Feature<LineString> = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: directions
          }
        }

        const offset = (index - (Object.keys(routeData).length - 1) / 2) * 2

        return (
          <Source
            key={`crew-route-${crewLeadEmail}`}
            id={`crew-route-${crewLeadEmail}`}
            type="geojson"
            data={routeGeoJSON}
          >
            <Layer
              id={`crew-route-line-${crewLeadEmail}`}
              type="line"
              paint={{
                'line-color': group.color,
                'line-width': 4,
                'line-opacity': 0.9,
                'line-offset': offset
              }}
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
            />
          </Source>
        )
      })}

      {singles.map((marker) => {
        const isHovered = hoveredEventId === marker.event.id || hoveredEventIds.includes(marker.event.id) || mapHoveredEventId === marker.event.id
        return (
          <ScheduleMapMarker
            key={marker.event.id}
            latitude={marker.latitude}
            longitude={marker.longitude}
            status={marker.status}
            crewLeadAvatar={marker.crewLeadAvatar}
            crewLeadName={marker.crewLeadName}
            crewLeadColor={marker.crewLeadColor}
            isHovered={isHovered}
            onClick={() => {
              setSelectedMarker(current => current?.event.id === marker.event.id ? null : marker)
            }}
            onMouseEnter={() => {
              setMapHoveredEventId(marker.event.id)
            }}
            onMouseLeave={() => {
              setMapHoveredEventId(null)
            }}
          />
        )
      })}

      {markerToShow && (
        <DayViewMarkerPopup
          event={markerToShow.event}
          latitude={markerToShow.latitude}
          longitude={markerToShow.longitude}
          visits={visits}
          calendarEventVisits={calendarEventVisits}
          workareaStatuses={workareaStatuses}
          date={date}
          markerStatus={markerToShow.status}
          crewLeadAvatar={markerToShow.crewLeadAvatar}
          crewLeadName={markerToShow.crewLeadName}
          crewLeadColor={markerToShow.crewLeadColor}
          mapRef={mapRef}
          onClose={() => setSelectedMarker(null)}
        />
      )}

      {clusters.map((cluster, index) => {
        const isListHovered = cluster.events.some(e =>
          e.id === hoveredEventId ||
          hoveredEventIds.includes(e.id)
        )
        const isMapHovered = cluster.events.some(e =>
          mapHoveredEventIds.includes(e.id)
        )
        return (
          <ScheduleMapClusterMarker
            key={`cluster-${cluster.latitude}-${cluster.longitude}-${index}`}
            latitude={cluster.latitude}
            longitude={cluster.longitude}
            events={cluster.events}
            address={cluster.address}
            isHovered={isListHovered || isMapHovered}
            showPopupOnHover={isListHovered}
            mapRef={mapRef}
            onMouseEnter={() => {
              setMapHoveredEventIds(cluster.events.map(e => e.id))
            }}
            onMouseLeave={() => {
              setMapHoveredEventIds([])
            }}
          />
        )
      })}

      {shopLocation?.latitude && shopLocation?.longitude && (
        <ShopMarker
          latitude={shopLocation.latitude}
          longitude={shopLocation.longitude}
        />
      )}

    </Map>
    <DayViewMapLegend />
    <EventAddressModal
      isOpen={selectedEventForAddress !== null}
      onClose={() => setSelectedEventForAddress(null)}
      event={selectedEventForAddress}
    />
    <ManageShopAddressModal
      isOpen={isManageShopAddressModalOpen}
      onClose={() => setIsManageShopAddressModalOpen(false)}
      shopAddress={shopLocation}
      onAddressUpdate={(coordinates) => {
        if (mapRef.current && eventsWithCoordinates.length === 0) {
          mapRef.current.flyTo({
            center: [coordinates.longitude, coordinates.latitude],
            zoom: DEFAULT_MAP_ZOOM,
            duration: 1000,
          })
        }
      }}
    />
  </div>
}

export default DayViewMap